import schedulingContextAtom from "@/atoms/scheduling-context"
import { getMerchantId } from "@/lib/company-storage"
import { fetchSchedulingContext } from "@/lib/scheduling/fetch-scheduling-context"
import { setTenantTimezone } from "@/lib/scheduling/tenant-timezone"
import { useSetAtom } from "jotai"
import { useCallback, useEffect, useRef } from "react"

const REFRESH_INTERVAL_MS = 60_000

let refreshHandle: (() => Promise<void>) | null = null

/** Forces an immediate re-read of the scheduling context, if the hook is mounted. */
export function refreshSchedulingContext(): Promise<void> {
  return refreshHandle ? refreshHandle() : Promise.resolve()
}

/**
 * Keeps the scheduling context fresh: on mount, whenever the tab regains
 * focus, and every 60 s while visible. The endpoint has no cache, so a stale
 * window or a timing the merchant just turned off never survives a minute.
 */
export function useSchedulingContext(enabled: boolean): void {
  const setContext = useSetAtom(schedulingContextAtom)
  const inFlight = useRef<AbortController | null>(null)

  const refresh = useCallback(async () => {
    const merchantId = getMerchantId()
    if (!merchantId) return

    inFlight.current?.abort()
    const controller = new AbortController()
    inFlight.current = controller

    try {
      const context = await fetchSchedulingContext(merchantId, controller.signal)
      if (controller.signal.aborted) return
      setTenantTimezone(context?.timezone)
      setContext(context)
    } catch {
      if (!controller.signal.aborted) setContext(null)
    }
  }, [setContext])

  useEffect(() => {
    refreshHandle = refresh
    return () => {
      if (refreshHandle === refresh) refreshHandle = null
    }
  }, [refresh])

  useEffect(() => {
    if (!enabled) return
    void refresh()

    function onVisible() {
      if (document.visibilityState === "visible") void refresh()
    }

    const interval = window.setInterval(() => {
      if (document.visibilityState !== "visible") return
      void refresh()
    }, REFRESH_INTERVAL_MS)

    document.addEventListener("visibilitychange", onVisible)
    window.addEventListener("focus", onVisible)

    return () => {
      window.clearInterval(interval)
      document.removeEventListener("visibilitychange", onVisible)
      window.removeEventListener("focus", onVisible)
      inFlight.current?.abort()
    }
  }, [enabled, refresh])
}
